import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { message } from 'antd';
import { Eye, EyeOff, Lock } from 'feather-icons-react/build/IconComponents';
import { authService } from '../../../services/authService';
import { useAuth } from '../../../contexts/AuthContext';
import { defaultHomePath } from '../../../lib/homePath';
import AuthLayout from './AuthLayout';
import styles from './Login.module.css';

const ChangePassword = () => {
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const isForcedChange = user?.must_change_password;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (newPassword.length < 8) {
      setError('La nueva contraseña debe tener al menos 8 caracteres.');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('Las contraseñas no coinciden.');
      return;
    }

    setLoading(true);
    try {
      await authService.changePassword(currentPassword, newPassword, confirmPassword);
      await refreshUser();
      message.success('Contraseña actualizada correctamente');
      navigate(defaultHomePath(user), { replace: true });
    } catch (err) {
      setError(err?.response?.data?.message || 'Error al cambiar la contraseña.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthLayout loading={loading}>
      <div className={styles.formHeader}>
        <h2>Cambiar contraseña</h2>
        <p>Elija una nueva contraseña para su cuenta</p>
      </div>

      {isForcedChange && (
        <div className="alert alert-warning mb-3" role="alert">
          <i className="fa fa-lock me-2"></i>
          Por seguridad, debe cambiar su contraseña antes de continuar.
        </div>
      )}

      {error && (
        <div className="alert alert-danger mb-3" role="alert">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} aria-busy={loading}>
        <div className={styles.field}>
          <label htmlFor="ec-current-password" className={styles.fieldLabel}>Contraseña actual</label>
          <div className={styles.fieldWrap}>
            <span className={styles.fieldIcon} aria-hidden="true">
              <Lock size={18} />
            </span>
            <input
              id="ec-current-password"
              type={showCurrent ? 'text' : 'password'}
              className={`${styles.input} ${styles.inputPassword}`}
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              disabled={loading}
              autoComplete="current-password"
              required
            />
            <button
              type="button"
              className={styles.eye}
              onClick={() => setShowCurrent((v) => !v)}
              tabIndex={-1}
              aria-label={showCurrent ? 'Ocultar contraseña' : 'Mostrar contraseña'}
            >
              {showCurrent ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>
        </div>

        <div className={styles.field}>
          <label htmlFor="ec-new-password" className={styles.fieldLabel}>Nueva contraseña</label>
          <div className={styles.fieldWrap}>
            <span className={styles.fieldIcon} aria-hidden="true">
              <Lock size={18} />
            </span>
            <input
              id="ec-new-password"
              type={showNew ? 'text' : 'password'}
              className={`${styles.input} ${styles.inputPassword}`}
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              disabled={loading}
              autoComplete="new-password"
              minLength={8}
              required
            />
            <button
              type="button"
              className={styles.eye}
              onClick={() => setShowNew((v) => !v)}
              tabIndex={-1}
              aria-label={showNew ? 'Ocultar contraseña' : 'Mostrar contraseña'}
            >
              {showNew ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>
          <small className="text-muted">Mínimo 8 caracteres</small>
        </div>

        <div className={styles.field}>
          <label htmlFor="ec-confirm-password" className={styles.fieldLabel}>Confirmar nueva contraseña</label>
          <div className={styles.fieldWrap}>
            <span className={styles.fieldIcon} aria-hidden="true">
              <Lock size={18} />
            </span>
            <input
              id="ec-confirm-password"
              type={showConfirm ? 'text' : 'password'}
              className={`${styles.input} ${styles.inputPassword}`}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              disabled={loading}
              autoComplete="new-password"
              required
            />
            <button
              type="button"
              className={styles.eye}
              onClick={() => setShowConfirm((v) => !v)}
              tabIndex={-1}
              aria-label={showConfirm ? 'Ocultar contraseña' : 'Mostrar contraseña'}
            >
              {showConfirm ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>
        </div>

        <button
          type="submit"
          className={styles.submit}
          disabled={loading}
        >
          {loading ? (
            <>
              <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
              Guardando...
            </>
          ) : (
            'Cambiar Contraseña'
          )}
        </button>
      </form>
    </AuthLayout>
  );
};

export default ChangePassword;
